import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Upload, CheckCircle2, Lock, FileText, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const Wrapper = ({ icon: Icon, title, children }) => (
  <div className="min-h-screen bg-beige flex items-center justify-center px-6 py-32">
    <div className="max-w-md text-center">
      <Icon size={48} strokeWidth={1.25} className="text-gold mx-auto mb-6" />
      <h1 className="font-serif text-3xl text-petrol mb-3">{title}</h1>
      {children}
    </div>
  </div>
);

function SignaturePad({ onChange, t }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#0F292F";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches?.[0];
    const x = (touch ? touch.clientX : e.clientX) - rect.left;
    const y = (touch ? touch.clientY : e.clientY) - rect.top;
    return { x: x * (canvasRef.current.width / rect.width), y: y * (canvasRef.current.height / rect.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };
  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div>
      <span className="overline text-[10px] text-muted-foreground">{t("checkin.signature")} *</span>
      <p className="text-xs text-muted-foreground mt-1 mb-2">{t("checkin.signature_hint")}</p>
      <canvas
        ref={canvasRef}
        width={500}
        height={160}
        data-testid="signature-pad"
        className="w-full border border-line rounded-sm bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <button type="button" onClick={clear} data-testid="signature-clear" className="text-xs text-muted-foreground mt-2 hover:text-petrol link-underline">
        {t("checkin.clear")}
      </button>
    </div>
  );
}

function FileUpload({ label, name, required, file, setFile, t }) {
  const inputRef = useRef(null);
  return (
    <div>
      <span className="overline text-[10px] text-muted-foreground">{label}{required && " *"}</span>
      <div
        onClick={() => inputRef.current?.click()}
        data-testid={`upload-${name}`}
        className="mt-2 border border-dashed border-line rounded-sm p-5 flex items-center gap-3 cursor-pointer hover:border-gold transition-colors bg-cream"
      >
        {file ? <FileText size={20} className="text-gold" /> : <Upload size={20} className="text-muted-foreground" strokeWidth={1.5} />}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-petrol truncate">{file ? file.name : t("checkin.upload_hint")}</p>
        </div>
        {file && (
          <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="text-muted-foreground hover:text-destructive">
            <X size={16} />
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        className="hidden"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
      />
    </div>
  );
}

export default function CheckIn() {
  const { token } = useParams();
  const { t } = useLang();
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState("loading"); // loading / ready / invalid / expired / completed / done
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", guests: 1,
    doc_type: "carta_identita", doc_number: "", doc_expiry: "", nationality: "",
  });
  const [signature, setSignature] = useState("");
  const [consent, setConsent] = useState(false);
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [extra, setExtra] = useState(null);

  const loadCheckin = (data) => {
    setInfo(data);
    setStatus(data.completed ? "completed" : "ready");
  };

  const fetchCheckin = () => {
    api.get(`/checkin/${token}`)
      .then((r) => loadCheckin(r.data))
      .catch((err) => setStatus(err.response?.status === 410 ? "expired" : "invalid"));
  };

  useEffect(() => {
    fetchCheckin();
  }, [token]);

  const upd = (k, v) => setForm({ ...form, [k]: v });

  // Submit check-in (multipart) via axios
  const submitAxios = async () => {
    if (!consent) { toast.error(t("common.required")); return; }
    if (!front) { toast.error(t("checkin.upload_front")); return; }
    if (!signature) { toast.error(t("checkin.signature")); return; }
    setSubmitting(true);
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    fd.append("signature", signature);
    fd.append("privacy_consent", "true");
    fd.append("doc_front", front);
    if (back) fd.append("doc_back", back);
    if (extra) fd.append("doc_extra", extra);
    try {
      await api.post(`/checkin/${token}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setStatus("done");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") return <Wrapper icon={Lock} title={t("common.loading")} />;
  if (status === "invalid") return <Wrapper icon={X} title={t("checkin.invalid")} />;
  if (status === "expired") return <Wrapper icon={Lock} title={t("checkin.expired")} />;
  if (status === "completed") return <Wrapper icon={CheckCircle2} title={t("checkin.completed")} />;
  if (status === "done")
    return (
      <Wrapper icon={CheckCircle2} title={t("checkin.success")}>
        <p className="text-muted-foreground mt-2">{info?.apartment_name}</p>
      </Wrapper>
    );

  const inputCls = "editorial-input mt-1 text-petrol";

  return (
    <div data-testid="checkin-page" className="min-h-screen bg-beige pt-28 pb-24 px-6">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <ShieldCheck size={40} strokeWidth={1.25} className="text-gold mx-auto mb-4" />
          <h1 className="font-serif font-light text-4xl text-petrol">{t("checkin.title")}</h1>
          <p className="text-muted-foreground mt-3">{t("checkin.subtitle")}</p>
        </div>

        {/* Stay summary */}
        <div className="bg-card border border-line rounded-sm p-5 mb-8 flex flex-wrap gap-6 justify-between text-sm">
          <div>
            <span className="overline text-[10px] text-muted-foreground block">{t("checkin.apartment")}</span>
            <span className="text-petrol font-medium">{info?.apartment_name}</span>
          </div>
          <div>
            <span className="overline text-[10px] text-muted-foreground block">{t("checkin.stay")}</span>
            <span className="text-petrol">{info?.arrival_date} → {info?.departure_date}</span>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((n) => (
            <React.Fragment key={n}>
              <div className="flex flex-col items-center gap-1.5 flex-1">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm transition-all ${step >= n ? "bg-petrol text-white" : "bg-white border border-line text-muted-foreground"}`}>
                  {n}
                </div>
                <span className="text-[10px] overline text-muted-foreground text-center">{t(`checkin.step${n}`)}</span>
              </div>
              {n < 3 && <div className={`h-px flex-1 ${step > n ? "bg-petrol" : "bg-line"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-card border border-line rounded-sm p-7 md:p-9">
          {step === 1 && (
            <div className="space-y-5" data-testid="checkin-step-1">
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("form.first_name")} *</span>
                  <input className={inputCls} value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} data-testid="checkin-first-name" /></label>
                <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("form.last_name")} *</span>
                  <input className={inputCls} value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} data-testid="checkin-last-name" /></label>
              </div>
              <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("form.email")} *</span>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="checkin-email" /></label>
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("form.phone")}</span>
                  <input className={inputCls} value={form.phone} onChange={(e) => upd("phone", e.target.value)} data-testid="checkin-phone" /></label>
                <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("form.guests")} *</span>
                  <input type="number" min={1} className={inputCls} value={form.guests} onChange={(e) => upd("guests", e.target.value)} data-testid="checkin-guests" /></label>
              </div>
              <button onClick={() => setStep(2)} data-testid="checkin-next-1" className="w-full bg-petrol text-white py-3.5 rounded-sm hover:bg-[#1A3A41] transition-colors mt-2">{t("checkin.next")}</button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="checkin-step-2">
              <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("checkin.doc_type")} *</span>
                <select className={inputCls} value={form.doc_type} onChange={(e) => upd("doc_type", e.target.value)} data-testid="checkin-doc-type">
                  <option value="carta_identita">{t("checkin.doc_id")}</option>
                  <option value="passaporto">{t("checkin.doc_passport")}</option>
                  <option value="patente">{t("checkin.doc_license")}</option>
                </select>
              </label>
              <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("checkin.doc_number")} *</span>
                <input className={inputCls} value={form.doc_number} onChange={(e) => upd("doc_number", e.target.value)} data-testid="checkin-doc-number" /></label>
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("checkin.doc_expiry")}</span>
                  <input type="date" className={inputCls} value={form.doc_expiry} onChange={(e) => upd("doc_expiry", e.target.value)} data-testid="checkin-doc-expiry" /></label>
                <label className="block"><span className="overline text-[10px] text-muted-foreground">{t("checkin.nationality")}</span>
                  <input className={inputCls} value={form.nationality} onChange={(e) => upd("nationality", e.target.value)} data-testid="checkin-nationality" /></label>
              </div>
              <div className="flex gap-3 mt-2">
                <button onClick={() => setStep(1)} className="flex-1 border border-petrol text-petrol py-3.5 rounded-sm hover:bg-beige transition-colors">{t("checkin.prev")}</button>
                <button onClick={() => setStep(3)} data-testid="checkin-next-2" className="flex-1 bg-petrol text-white py-3.5 rounded-sm hover:bg-[#1A3A41] transition-colors">{t("checkin.next")}</button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="checkin-step-3">
              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-cream border border-line rounded-sm p-3">
                <Lock size={14} className="text-gold mt-0.5 shrink-0" /> {t("checkin.privacy_note")}
              </div>
              <FileUpload label={t("checkin.upload_front")} name="front" required file={front} setFile={setFront} t={t} />
              <FileUpload label={t("checkin.upload_back")} name="back" file={back} setFile={setBack} t={t} />
              <FileUpload label={t("checkin.upload_extra")} name="extra" file={extra} setFile={setExtra} t={t} />
              <SignaturePad onChange={setSignature} t={t} />
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="checkin-consent" className="mt-1 accent-[#0F292F]" />
                <span className="text-sm text-petrol/80">{t("checkin.consent")}</span>
              </label>
              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 border border-petrol text-petrol py-3.5 rounded-sm hover:bg-beige transition-colors">{t("checkin.prev")}</button>
                <button onClick={submitAxios} disabled={submitting} data-testid="checkin-submit" className="flex-1 bg-gold text-petrol font-medium py-3.5 rounded-sm hover:bg-[#c4a030] transition-colors disabled:opacity-60">
                  {submitting ? t("form.sending") : t("checkin.submit")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
